import { MatDialog } from '@angular/material/dialog';
import { IEntity, IMergeFile, INamedEntity } from 'interfaces-lib';
import { WorkflowMessage, WorkflowStatusMessage } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { IFileToPathConverter } from '../interfaces/ifiletopathconverter';
import { IFileMergeController } from '../merge-basket-view/merge-basket-view.component';
import { IS3MergeFile } from '../merge-component/merge-controller.service';
import { buildSalesforceContactDocumentParameters } from '../utils/salesforce-contact-document-helpers';
import { Stage, StageSet } from '../workflow-in-progress/workflow-in-progress.component';
import { ProgressReportingWorkflowTask } from './progress-reporting-workflow-task';

export class MergeFilesTask extends ProgressReportingWorkflowTask {
  private static readonly STAGES: StageSet = {
    MergeStarted: { text: 'Merge process started', icon: 'merge_type' },
    MergingFiles: { text: 'Merging files', icon: 'merge_type' },
    RetrievingMergedFile: {
      text: 'Retrieving merged file',
      icon: 'cloud_download',
    },
  };
  private readonly startParams: any;
  private readonly closingStage: Stage;

  constructor(
    private namespace: string,
    private outputFilename: string,
    private entity: IEntity,
    private files: (IMergeFile & IS3MergeFile)[],
    private fileToPathConverter: IFileToPathConverter,
    private fileMergeController: IFileMergeController,
    dialog: MatDialog,
    private messageNotificationService: MessageNotificationService,
    private workflowSource: string,
  ) {
    super('DocumentMerge', `Merging files to ${outputFilename}`, MergeFilesTask.STAGES, dialog, { startStage: 'MergeStarted' });
    const owner = MergeFilesTask.isNamedEntity(entity) ? entity.name : 'record';
    this.closingStage = {
      text: `Merge complete and saved to ${owner}`,
      icon: 'done',
    };
    this.startParams = this.buildStartParams(namespace, entity, outputFilename, files, workflowSource);
  }

  override getStartParams(): any {
    return this.startParams;
  }

  override receive(message: WorkflowMessage): boolean {
    if (message.type === 'ErrorEvent') {
      this.closeProgress();
      this.fileMergeController.fileMergeError(this.entity, message.message);
      return true;
    }

    if (message.type === 'FileMergeStatus') {
      const statusMessage = <WorkflowStatusMessage>message.message;
      this.updateProgress(statusMessage);
      this.fileMergeController.fileMergeUpdate(this.entity, statusMessage.status);
      return false;
    }

    if (message.type === 'UploadFileResponse') {
      this.closeProgress(ProgressReportingWorkflowTask.DEFAULT_CLOSE_DELAY_MS, this.closingStage);
      this.messageNotificationService.info('Files have been merged', `${this.outputFilename} has been saved to your record.`);
      this.fileMergeController.fileMergeComplete(this.entity);
      return true;
    }

    console.debug(`Message '${message.type}' not handled.`);

    return false;
  }

  private buildStartParams(
    namespace: string,
    entity: IEntity,
    outputFilename: string,
    files: (IMergeFile & IS3MergeFile)[],
    workflowSource: string,
  ): any {
    const startParams = {
      WORKFLOW_SOURCE: workflowSource,
      SOURCE_LIST: [],
      DOCMERGE_DESTINATION: {
        datasource: {
          namespace: namespace,
          path: [entity.type, entity.id],
          filename: outputFilename,
        },
      },
    };

    const alphaOrder = [];
    for (let i = 0; i < files.length; i++) {
      alphaOrder.push(i + '_');
    }
    alphaOrder.sort();

    startParams.SOURCE_LIST = files.map((file, index) => {
      const order = alphaOrder[index];
      if (file.s3) {
        return this.mapS3FileToSource(file, order);
      }

      return this.mapEntityFileToSource(namespace, file, order);
    });

    console.log('merge startParams', startParams);

    return startParams;
  }

  private mapEntityFileToSource(namespace: string, file: IMergeFile, order: string): any {
    const source: any = {
      order: order,
      namespace: namespace,
      path: this.fileToPathConverter.convert(file),
    };
    const parameters = buildSalesforceContactDocumentParameters(namespace, file);
    if (parameters) {
      source.parameters = parameters;
    }
    return source;
  }

  private mapS3FileToSource(file: IS3MergeFile, order: string): any {
    return {
      order: order,
      namespace: file.s3.namespace,
      path: file.s3.path,
    };
  }

  private static isNamedEntity(e: IEntity | INamedEntity): e is INamedEntity {
    return e && 'name' in e;
  }
}
